// fractions.js - Fraction problem generator
import { EquivalentFractionProblem, MixedMultiplicationProblem } from './ui.js';
import { getRandomInt } from './utils.js';

// function findGCD(a, b) {
//     while (b !== 0) {
//         const temp = b;
//         b = a % b;
//         a = temp;
//     }
//     return a;
// }

// First add a GCD helper function
function findGCD(a: number, b: number): number {
    while (b !== 0) {
        const temp = b;
        b = a % b;
        a = temp;
    }
    return Math.abs(a);
}

function generateMultiplier() {
    // Generate a random multiplier (2, 3, 4, 5, 6, 8, or 10)
    const commonMultipliers = [2, 3, 4, 5, 6, 8, 10];
    return commonMultipliers[Math.floor(Math.random() * commonMultipliers.length)];
}

export function generateEquivalentFractionProblem(): EquivalentFractionProblem {
    // First, generate a reduced fraction
    let denominator = getRandomInt(2, 12);
    let numerator = getRandomInt(1, denominator - 1);

    // Make sure the fraction is in its simplest form
    const gcd = findGCD(numerator, denominator);
    numerator = numerator / gcd;
    denominator = denominator / gcd;

    // Now multiply both by a common factor to create an equivalent fraction
    const multiplier = generateMultiplier();
    const finalNumerator = numerator * multiplier;
    const finalDenominator = denominator * multiplier;

    return new EquivalentFractionProblem(
        String(finalNumerator).padStart(2, ' '),
        String(finalDenominator).padStart(2, ' '),
        '',
        {
            top: String(numerator),
            bottom: String(denominator)
        }
    );
}


function simplifyFraction(numerator: number, denominator: number): any {
    const gcd = findGCD(numerator, denominator);
    return {
        numerator: numerator / gcd,
        denominator: denominator / gcd
    };
}

function generateMixedMultiplicationProblem(): MixedMultiplicationProblem {
    const wantWholeNumbers = document.querySelector<HTMLInputElement>('input[name="resultType"]:checked')?.value === 'wholeNumbers';

    if (wantWholeNumbers) {
        const denominator = getRandomInt(2, 12);
        const multiplier = denominator * getRandomInt(1, 5);

        // Find valid numerators that work with our multiplier and denominator
        const validNumerators = [];
        for (let n = 1; n < denominator; n++) {
            if ((n * multiplier) % denominator === 0) {
                validNumerators.push(n);
            }
        }

        if (validNumerators.length > 0) {
            const numerator = validNumerators[getRandomInt(0, validNumerators.length - 1)];
            const wholeNumber = getRandomInt(1, 5);

            // Simplify the fraction before returning
            const simplified = simplifyFraction(numerator, denominator);

            return new MixedMultiplicationProblem(
                {
                    whole: wholeNumber,
                    numerator: simplified.numerator,
                    denominator: simplified.denominator
                },
                '×',
                multiplier
            );
        }

        // Fallback case (already simplified)
        const simpleMultiplier = getRandomInt(2, 4);
        return new MixedMultiplicationProblem(
            {
                whole: getRandomInt(1, 3),
                numerator: 1,
                denominator: simpleMultiplier
            },
            '×',
            simpleMultiplier
        );
    } else {
        // For non-whole number results, still simplify the fraction
        const wholeNumber = getRandomInt(1, 5);
        const denominator = getRandomInt(2, 12);
        const maxMultiplier = 2 * denominator;
        const numerator = getRandomInt(1, denominator - 1);
        let multiplier = getRandomInt(2, maxMultiplier);

        // Keep regenerating multiplier as long as denominator divides evenly
        while ((multiplier * numerator) % denominator === 0) {
            multiplier = getRandomInt(2, maxMultiplier);
        }

        // Simplify the fraction before returning
        const simplified = simplifyFraction(numerator, denominator);

        return new MixedMultiplicationProblem(
            {
                whole: wholeNumber,
                numerator: simplified.numerator,
                denominator: simplified.denominator
            },
            '×',
            multiplier
        );
    }
}

export function generateFractionProblem(): EquivalentFractionProblem | MixedMultiplicationProblem {
    const type = document.querySelector<HTMLInputElement>('input[name="fractionsType"]:checked')?.value;
    if (type === 'equivalent') {
        return generateEquivalentFractionProblem();
    } else if (type === 'mixedMultiplication') {
        return generateMixedMultiplicationProblem();
    }
    return generateEquivalentFractionProblem(); // Default fallback
}